import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Send, Warehouse } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const WEEKDAYS = [["0", "Lunedì"], ["1", "Martedì"], ["2", "Mercoledì"], ["3", "Giovedì"], ["4", "Venerdì"], ["5", "Sabato"], ["6", "Domenica"]];
const asList = (s) => (s || "").split(/[\n,;]+/).map((x) => x.trim()).filter((x) => x.includes("@"));
const dowLabel = (d) => (WEEKDAYS.find(([v]) => v === String(d)) || ["", "—"])[1];

export default function MagazzinoFunnelDialog({ open, onOpenChange }) {
  const [cfg, setCfg] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [recipientsText, setRecipientsText] = useState("");
  const [ccText, setCcText] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/magazzino-funnel-config").then((r) => {
      setCfg(r.data.config); setCanEdit(!!r.data.can_edit);
      setRecipientsText((r.data.config.recipients || []).join("\n"));
      setCcText((r.data.config.cc || []).join(", "));
    }).catch(() => toast.error("Errore caricamento configurazione")).finally(() => setLoading(false));
  }, [open]);

  const upd = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    setBusy("save");
    try {
      const r = await api.put("/magazzino-funnel-config", { config: cfg });
      setCfg(r.data.config);
      toast.success("Funnel magazzino salvato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(""); }
  };

  const inviaProva = async () => {
    setBusy("test");
    try {
      const r = await api.post("/magazzino-funnel-config/invia-prova", { email: testEmail || undefined });
      toast.success(`Prova (dati reali) inviata a ${r.data.to} — arriva tra 1-2 minuti con i 2 PDF allegati`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio prova fallito"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="mag-funnel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Warehouse className="h-5 w-5 text-primary" /> Funnel Magazzino</DialogTitle>
          <DialogDescription>Report settimanale del magazzino (fiscale CSV e articoli a listino) con proiezione al 31/12 e riepilogo riallocazioni. Allega PDF riunione pianificazioni (forecast 12 mesi) e magazzino produzione al 31/12.</DialogDescription>
        </DialogHeader>
        {loading || !cfg ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4">
            {(() => {
              const armed = !!cfg.enabled && (cfg.recipients || []).length > 0;
              return (
                <div className={`rounded-md border p-2.5 text-xs ${armed ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300"}`} data-testid="mag-funnel-status">
                  {armed
                    ? <>✅ <b>Invio automatico ATTIVO</b> — prossimo invio <b>{dowLabel(cfg.send_day ?? 2)}</b> alle <b>{cfg.send_time || "08:30"}</b> a {(cfg.recipients || []).length} destinatario/i.</>
                    : <>⚠️ <b>Invio automatico SPENTO.</b> Per attivarlo: aggiungi almeno un destinatario, accendi il toggle "Invio automatico attivo" e premi <b>Salva</b>. (La "Invia prova" funziona comunque anche da spento.)</>}
                </div>
              );
            })()}
            <div className="flex items-center justify-between rounded-md border border-input p-2.5">
              <Label className="text-sm font-medium">Invio automatico attivo</Label>
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => upd({ enabled: v })} disabled={!canEdit} data-testid="mag-funnel-enabled" />
            </div>

            <div>
              <Label className="text-xs">Oggetto email</Label>
              <Input value={cfg.subject || ""} onChange={(e) => upd({ subject: e.target.value })} disabled={!canEdit} data-testid="mag-funnel-subject" className="h-9" />
            </div>

            <div>
              <Label className="text-xs">Destinatari (uno per riga)</Label>
              <textarea className="w-full rounded-md border border-input bg-background p-2 text-sm" rows={2} data-testid="mag-funnel-recipients" disabled={!canEdit}
                value={recipientsText} onChange={(e) => { setRecipientsText(e.target.value); upd({ recipients: asList(e.target.value) }); }} placeholder="direzione@torrisolare.it" />
            </div>
            <div>
              <Label className="text-xs">In copia (CC)</Label>
              <Input value={ccText} data-testid="mag-funnel-cc" disabled={!canEdit} onChange={(e) => { setCcText(e.target.value); upd({ cc: asList(e.target.value) }); }} placeholder="cc1@..., cc2@..." className="h-9" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Giorno invio</Label>
                <Select value={String(cfg.send_day ?? 2)} onValueChange={(v) => upd({ send_day: Number(v) })} disabled={!canEdit}>
                  <SelectTrigger data-testid="mag-funnel-weekday" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{WEEKDAYS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Orario</Label>
                <Input type="time" value={cfg.send_time || "08:30"} onChange={(e) => upd({ send_time: e.target.value })} disabled={!canEdit} data-testid="mag-funnel-time" className="h-9" />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-input pt-3">
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="email per la prova" className="h-9 w-52" data-testid="mag-funnel-test-email" />
              <Button type="button" variant="outline" size="sm" onClick={inviaProva} disabled={busy === "test"} data-testid="mag-funnel-test-send">
                {busy === "test" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Invia prova
              </Button>
              {canEdit && (
                <Button type="button" size="sm" onClick={save} disabled={busy === "save"} className="ml-auto" data-testid="mag-funnel-save">
                  {busy === "save" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Salva
                </Button>
              )}
            </div>
            {!canEdit && <p className="text-[11px] text-amber-600 dark:text-amber-300">Sola lettura: solo la Direzione può modificare e salvare. La prova usa i dati reali attuali.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
